import React from 'react';
import { motion } from 'framer-motion';
import { Card } from '../ui/card';
import { 
  Lock, 
  Shield, 
  Globe, 
  LucideIcon
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface TrustSignal {
  icon: LucideIcon;
  title: string;
  subtitle: string;
  color: string;
}

interface TrustSignalsProps {
  // Content Props
  title?: string;
  subtitle?: string;
  
  // Data Props
  signals?: TrustSignal[];
  
  // Display Props
  columns?: {
    mobile: number;
    tablet: number;
    desktop: number;
  };
  
  // Style Props
  className?: string;
  variant?: 'default' | 'compact' | 'detailed';
  
  // Animation Props
  enableAnimations?: boolean;
  staggerDelay?: number;
}

const TrustSignals: React.FC<TrustSignalsProps> = ({
  title,
  subtitle,
  signals,
  columns = { mobile: 2, tablet: 3, desktop: 6 },
  className = "",
  variant = 'default',
  enableAnimations = true,
  staggerDelay = 0.1,
  ...props
}) => {
  const { t } = useTranslation();

  // Default content with i18n
  const defaultTitle = title || t('enterpriseSecurity.title', { ns: 'homepage' });
  const defaultSubtitle = subtitle || t('enterpriseSecurity.description', { ns: 'homepage' });
  
  const defaultSignals: TrustSignal[] = signals || [
    { 
      icon: Lock, 
      title: t('trustSignals.encrypted.title', { ns: 'homepage' }), 
      subtitle: t('trustSignals.encrypted.subtitle', { ns: 'homepage' }), 
      color: "text-green-600" 
    },
    { 
      icon: Shield, 
      title: t('trustSignals.tenantIsolation.title', { ns: 'homepage' }), 
      subtitle: t('trustSignals.tenantIsolation.subtitle', { ns: 'homepage' }), 
      color: "text-purple-600" 
    },
    { 
      icon: Globe, 
      title: t('trustSignals.permissions.title', { ns: 'homepage' }), 
      subtitle: t('trustSignals.permissions.subtitle', { ns: 'homepage' }), 
      color: "text-orange-600" 
    },
  ];

  // Animation variants
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4 }
    },
    hover: { 
      y: -4,
      transition: { duration: 0.2 }
    }
  };

  // Variant classes
  const variantClasses = {
    default: "space-y-8",
    compact: "space-y-6",
    detailed: "space-y-10"
  };

  const gridClasses = `grid grid-cols-${columns.mobile} md:grid-cols-${columns.tablet} lg:grid-cols-${columns.desktop} gap-6 max-w-4xl mx-auto`;

  // Motion components
  const MotionSection = enableAnimations ? motion.section : 'section';
  const MotionDiv = enableAnimations ? motion.div : 'div';

  return (
    <MotionSection 
      variants={enableAnimations ? itemVariants : undefined}
      initial={enableAnimations ? "hidden" : undefined}
      animate={enableAnimations ? "visible" : undefined}
      className={cn(variantClasses[variant], className)}
      {...props}
    >
      {/* Section Header */}
      <div className="text-center space-y-4">
        <h2 className="text-3xl font-bold text-foreground">{defaultTitle}</h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          {defaultSubtitle}
        </p>
      </div>

      {/* Trust Signals Grid */}
      <div className={gridClasses}>
        {defaultSignals.map((signal, index) => (
          <MotionDiv
            key={signal.title}
            variants={enableAnimations ? cardVariants : undefined}
            initial={enableAnimations ? "hidden" : undefined}
            animate={enableAnimations ? "visible" : undefined}
            transition={enableAnimations ? { delay: index * staggerDelay } : undefined}
            whileHover={enableAnimations ? "hover" : undefined}
          >
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <div className={cn("w-12 h-12 rounded-lg flex items-center justify-center bg-muted mx-auto mb-4", signal.color)}>
                <signal.icon className="w-6 h-6" />
              </div>
              <div className="font-semibold text-sm">{signal.title}</div>
              <div className="text-xs text-muted-foreground">{signal.subtitle}</div>
            </Card>
          </MotionDiv>
        ))}
      </div>
    </MotionSection>
  );
};

export default TrustSignals;
export type { TrustSignalsProps, TrustSignal };
